"use client"

import { useRef, useEffect } from "react"
import { Leaf, Droplets, CalendarDays } from "lucide-react"
import { GRASS_BLADES } from "@/components/guides/grass-blades"

const GrassBlade = GRASS_BLADES["kentucky-bluegrass"]
import { ChatBubble } from "./chat-bubble"
import { getToolLabel, type ChatMessage, type ChatError } from "@/hooks/use-chat"
import Link from "next/link"

interface ChatMessagesProps {
  messages: ChatMessage[]
  activeTools: string[]
  error: ChatError | null
  onSend: (content: string) => void
  onClearError: () => void
}

const STARTERS = [
  { text: "What grass type should I plant?", Icon: Leaf },
  { text: "How much should I water my lawn?", Icon: Droplets },
  { text: "When should I fertilize?", Icon: CalendarDays },
]

export function ChatMessages({
  messages,
  activeTools,
  error,
  onSend,
  onClearError,
}: ChatMessagesProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth" })
  }, [messages, activeTools, error])

  const isEmpty = messages.length === 0
  const isResponding = messages.some((message) => message.role === "assistant" && message.isStreaming)
  const activityAnnouncement = error
    ? ""
    : activeTools.length > 0
      ? `${getToolLabel(activeTools[activeTools.length - 1])}.`
      : isResponding
        ? "Lawn Care Expert is responding."
        : messages.length > 0
          ? "Response ready."
          : ""
  return (
    <div
      className="lcw-body"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      aria-busy={isResponding || activeTools.length > 0}
      aria-label="Chat messages"
    >
      <span className="sr-only" role="status" aria-live="polite">
        {activityAnnouncement}
      </span>
      {isEmpty && !error && (
        <div className="lcw-intro">
          <span className="lcw-intro__av">
            <GrassBlade className="w-9 h-9" color="#15803d" />
          </span>
          <div className="lcw-intro__title">Hi, I&apos;m your lawn care assistant</div>
          <div className="lcw-intro__sub">
            Ask me anything about your lawn, or start with one of these.
          </div>
          <div className="lcw-suggest">
            {STARTERS.map(({ text, Icon }) => (
              <button
                key={text}
                onClick={() => onSend(text)}
                className="lcw-chip"
              >
                <Icon size={16} aria-hidden="true" />
                <span>{text}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {messages.map((msg) => (
        <ChatBubble key={msg.id} message={msg} />
      ))}

      {activeTools.length > 0 && (
        <div className="lcw-toolstatus" role="status" aria-live="polite">
          <div className="lcw-typing" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <span className="lcw-toolstatus__label">
            {getToolLabel(activeTools[activeTools.length - 1])}...
          </span>
        </div>
      )}

      {error && (
        <div role="alert" aria-live="assertive" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm">
          {error.signupRequired ? (
            <div className="space-y-2">
              <p className="text-gray-800 font-medium">
                You&apos;ve reached the free message limit
              </p>
              <p className="text-gray-600 text-xs">
                Create a free account to keep chatting.
              </p>
              <Link
                href="/auth/login"
                className="inline-block mt-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Sign up free
              </Link>
            </div>
          ) : error.upgradeRequired ? (
            <div className="space-y-2">
              <p className="text-gray-800 font-medium">Chat limit reached</p>
              <p className="text-gray-600 text-xs">
                Upgrade to Pro for unlimited conversations.
              </p>
              <Link
                href="/dashboard/settings"
                className="inline-block mt-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                Upgrade
              </Link>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2">
              <p className="text-red-700">{error.message}</p>
              <button
                onClick={onClearError}
                className="text-xs text-red-600 hover:text-red-800 underline flex-shrink-0"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  )
}
